import React, { useState, useEffect } from "react";
import { fetchOrgBusinessOwnershipType } from "../../../Apis/BusinessOwnershipType.js";
import { Camera, Check, CheckCircle2, ChevronDown } from "lucide-react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls.js";

function orgInitials(name) {
  const src = String(name || "").trim();
  if (!src) return "ORG";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function OPField({ label, req, error, children }) {
  return (
    <div className="ems-fm-field">
      <label>
        {label}
        {req && <span className="ems-fm-req">*</span>}
      </label>
      {children}
      {error ? (
        <span style={{ fontSize: 11.5, color: "var(--ems-red)", fontWeight: 600 }}>{error}</span>
      ) : null}
    </div>
  );
}

function OrganizationProfile() {
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const [formErrors, setFormErrors] = useState({});
  const [btnLoading, setbtnLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [BusinessOwnershipType, setBusinessOwnershipType] = useState([]);

  const [profile, setProfile] = useState({
    organization_id: "",
    website: "",
    email: "",
    phone: "",
    logo_file: null,
    logo_url: "",
    establishment_date: "",
    number_of_employees: "",
    gst_number: "",
    organization_business_ownership_type_id: "",
    general_industry_id: "",
    general_business_ownership_type_category_id: "",
  });

  const fetchOrganizationProfile = async () => {
    if (!org?.organization_id) return;
    try {
      const response = await axios.get(
        `${MAIN_URL}/api/organizations/${org.organization_id}/business-identity-profile`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = response?.data?.profile ?? {};
      setProfile((prev) => ({
        ...prev,
        ...data,
        logo_file: null,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (org?.organization_id) {
      fetchOrganizationProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org) return;

    setProfile((prev) => ({
      ...prev,
      organization_id: org.organization_id || "",
    }));

    fetchOrgBusinessOwnershipType(org.organization_id)
      .then((data) => {
        setBusinessOwnershipType(data?.businessownershiptype);
      })
      .catch((err) => {
        setFormErrors({ fetch: err.message });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.organization_id]);

  const validateForm = () => {
    const newErrors = {};
    const urlRegex =
      /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]+$/;

    if (profile.website.trim()) {
      if (!urlRegex.test(profile.website.trim())) {
        newErrors.website = "Website must be a valid URL.";
      }
    }

    if (!profile.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!emailRegex.test(profile.email.trim())) {
      newErrors.email = "Email must be a valid email address.";
    }

    if (!profile.phone.trim()) {
      newErrors.phone = "Phone is required.";
    } else if (!phoneRegex.test(profile.phone.trim())) {
      newErrors.phone = "Phone must contain digits only.";
    } else if (profile.phone?.length < 10) {
      newErrors.phone = "Phone must be of 10 digits.";
    }

    const gst = String(profile.gst_number || "").trim().toUpperCase();
    if (gst) {
      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstRegex.test(gst)) {
        newErrors.gst_number = "Enter a valid 15-character GSTIN.";
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
    setSaved(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewURL = URL.createObjectURL(file);
    setProfile((prev) => ({
      ...prev,
      logo_file: file,
      logo_url: previewURL,
    }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const formData = new FormData();
    formData.append("organization_id", profile.organization_id);
    formData.append("website", profile.website);
    formData.append("email", profile.email);
    formData.append("phone", profile.phone);
    if (profile.establishment_date) {
      formData.append("establishment_date", profile.establishment_date);
    }
    if (profile.number_of_employees !== "" && profile.number_of_employees !== null) {
      formData.append("number_of_employees", profile.number_of_employees);
    }
    if (profile.gst_number !== undefined && profile.gst_number !== null) {
      formData.append("gst_number", String(profile.gst_number).trim().toUpperCase());
    }
    formData.append(
      "organization_business_ownership_type_id",
      profile.organization_business_ownership_type_id ?? ""
    );
    if (profile.logo_file instanceof File) {
      formData.append("logo_url", profile.logo_file);
    }

    setbtnLoading(true);
    try {
      await axios.post(
        `${MAIN_URL}/api/organizations/${org.organization_id}/business-identity-profile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Organization Profile saved successfully!");
      setFormErrors({});
      setSaved(true);
      await fetchOrganizationProfile();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages = Object.values(validationErrors)
          .map((arr) => arr[0])
          .join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    }
    setbtnLoading(false);
  };

  return (
    <div className="ems-dashboard ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Organization Profile</h1>
          <div className="ems-page-sub">
            <span>Organization Setup</span>
            {org?.organization_name ? (
              <>
                <span className="ems-dot"></span>
                <span>{org.organization_name}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className="ems-section-divider" />

      <form className="ems-card ems-op-card" onSubmit={handleSubmit}>
        <div className="ems-op-logo-row">
          <span className="ems-op-logo">
            {profile.logo_url ? <img src={profile.logo_url} alt="Organization logo" /> : orgInitials(org?.organization_name)}
          </span>
          <label className="ems-op-cam" title="Change logo">
            <Camera size={17} />
            <input hidden accept="image/*" type="file" onChange={handleImageUpload} />
          </label>
          <div className="ems-op-logo-meta">
            <div className="ems-op-logo-name">Organization Logo</div>
            <div className="ems-op-logo-hint">PNG or JPG, square, up to 2 MB.</div>
          </div>
        </div>

        <div className="ems-op-section">Contact</div>
        <div className="ems-fm-grid">
          <OPField label="Website" error={formErrors.website}>
            <input
              className="ems-fm-input"
              name="website"
              maxLength={255}
              value={profile.website}
              onChange={handleProfileChange}
            />
          </OPField>
          <OPField label="Email" req error={formErrors.email}>
            <input
              className="ems-fm-input"
              name="email"
              maxLength={100}
              value={profile.email}
              onChange={handleProfileChange}
            />
          </OPField>
          <OPField label="Phone" req error={formErrors.phone}>
            <input
              className="ems-fm-input"
              name="phone"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={25}
              value={profile.phone}
              onChange={(e) => {
                const input = e.target.value;
                if (/^\d{0,15}$/.test(input)) {
                  handleProfileChange(e);
                }
              }}
            />
          </OPField>
        </div>

        <div className="ems-op-section">Business</div>
        <div className="ems-fm-grid">
          <OPField label="Business Ownership Type" error={formErrors.organization_business_ownership_type_id}>
            <div className="ems-fm-select-wrap">
              <select
                className="ems-fm-input"
                name="organization_business_ownership_type_id"
                value={profile.organization_business_ownership_type_id || ""}
                onChange={handleProfileChange}
                disabled={BusinessOwnershipType?.length === 0}
              >
                <option value="">Select ownership type</option>
                {(BusinessOwnershipType || []).map((opt) => (
                  <option
                    key={opt.organization_business_ownership_type_id}
                    value={opt.organization_business_ownership_type_id}
                  >
                    {opt.organization_business_ownership_type_name}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} />
            </div>
          </OPField>
          <OPField label="Business Category">
            <input
              className="ems-fm-input"
              readOnly
              value={profile?.general_category?.business_ownership_type_category_name || ""}
            />
          </OPField>
          <OPField label="Industry Type">
            <input
              className="ems-fm-input"
              readOnly
              value={profile?.general_industry?.industry_name || ""}
            />
          </OPField>
          <OPField label="Number of Employees" error={formErrors.number_of_employees}>
            <input
              className="ems-fm-input"
              name="number_of_employees"
              type="number"
              min={1}
              value={profile.number_of_employees ?? ""}
              onChange={handleProfileChange}
            />
          </OPField>
          <OPField label="GST Number" error={formErrors.gst_number}>
            <input
              className="ems-fm-input"
              name="gst_number"
              type="text"
              maxLength={15}
              placeholder="e.g. 03AAAAA0000A1Z5"
              value={profile.gst_number ?? ""}
              onChange={handleProfileChange}
              style={{ textTransform: "uppercase" }}
            />
          </OPField>
          <OPField label="Establishment Date" error={formErrors.establishment_date}>
            <input
              className="ems-fm-input"
              name="establishment_date"
              type="date"
              value={profile.establishment_date || ""}
              onChange={handleProfileChange}
            />
          </OPField>
        </div>

        <div className="ems-op-foot">
          {saved && (
            <span className="ems-op-saved">
              <CheckCircle2 size={16} />
              All changes saved
            </span>
          )}
          <div className="ems-op-foot-btns">
            <button type="submit" className="ems-btn ems-primary" disabled={btnLoading}>
              {btnLoading ? "Saving…" : (<><Check size={16} />Save Profile</>)}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default OrganizationProfile;
